import { Plus, ArrowRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getEnvironment, lessonPlanningToolsList } from "@packages/utils";
import { Text } from "@core/text";
import { motion } from "framer-motion";

const LessonPlanningTools = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const isTed = window.location.href.includes("ted.madlen.io");

  const pageTitle = t("sidebar.menu.lessonPlanningTools.title");
  // Filter tools based on language
  const filteredTools = lessonPlanningToolsList.filter((tool) => {
    if (tool.id === "ted-plan") {
      return isTed;
    }
    if (i18n.language !== "tr") {
      // For non-Turkish languages, only show "Daily Lesson Plan"
      return tool.id === "daily-plan";
    }

    if (getEnvironment() === "DEV") {
      return true;
    }

    if (tool.id === "ubd-maarif-plan") return false;

    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="container mx-auto px-4 md:px-8">
        <header className="mb-10 text-center">
          <Text
            variant="h1"
            className="text-3xl md:text-4xl font-bold mb-3 text-gray-900 dark:text-gray-100"
          >
            {pageTitle}
          </Text>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-7xl mx-auto">
          {filteredTools.map((tool, index) => (
            <motion.div
              key={tool.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className={`
                flex flex-col rounded-xl shadow-lg overflow-hidden group 
                border border-gray-200 dark:border-gray-700 
                hover:shadow-2xl transition-all duration-300
                bg-white dark:bg-gray-800 
              `}
            >
              <div
                onClick={() => navigate(tool.path)}
                className="p-6 flex-grow cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors duration-200"
              >
                <div className="flex items-center mb-4">
                  <div
                    className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 flex-shrink-0 bg-gradient-to-br ${tool.colors.gradient}`}
                  >
                    <tool.icon className="w-7 h-7 text-white" />
                  </div>
                  <Text
                    variant="h3"
                    className={`text-xl font-semibold ${tool.colors.menuColor || "text-gray-800 dark:text-gray-100"}`}
                  >
                    {t(tool.title)}
                  </Text>
                </div>
                <Text
                  variant="p"
                  status="secondary"
                  className="text-sm mb-4 min-h-[60px]"
                >
                  {t(tool.description)}
                </Text>
              </div>

              <div className="px-6 pb-6 pt-4 border-t border-gray-200 dark:border-gray-700/50 bg-gray-50 dark:bg-gray-800/40">
                <Link
                  to={`${tool.path}/new`}
                  className={`w-full flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-medium
                  text-white bg-gradient-to-r ${tool.colors.gradient} 
                  hover:shadow-md hover:opacity-90 transition-all duration-300`}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {t("common.create")}
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LessonPlanningTools;
